import React from 'react'

import MeetingDatePicker from '@/components/integration/nylas/scheduler/components/MeetingDatePicker'
import ClientHomeAddressFields from '@/components/integration/nylas/scheduler/components/meetingFormatPicker/ClientHomeAddressFields'
import InPersonLocationPicker from '@/components/integration/nylas/scheduler/components/meetingFormatPicker/InPersonLocationPicker'
import InPersonPlacePicker from '@/components/integration/nylas/scheduler/components/meetingFormatPicker/InPersonPlacePicker'
import MeetingFormatPicker from '@/components/integration/nylas/scheduler/components/meetingFormatPicker/MeetingFormatPicker'
import MeetingSummary from '@/components/integration/nylas/scheduler/components/summary/MeetingSummary'
import TimeslotPicker from '@/components/integration/nylas/scheduler/components/timeslotPicker/TimeslotPicker'
import TimezonePicker from '@/components/integration/nylas/scheduler/components/timezonePicker/TimezonePicker'
import { SchedulerConfigContext } from '@/components/integration/nylas/scheduler/context/SchedulerConfigContext'
import { SchedulerSelectionContext } from '@/components/integration/nylas/scheduler/context/SchedulerSelectionContext'
import {
  isBookableMeetingFormat,
  isClientHomeAddressComplete,
  isInPersonMeetingFormat,
} from '@/components/integration/nylas/scheduler/helpers'
import useSchedulerState from '@/components/integration/nylas/scheduler/hooks/useSchedulerState'
import {
  ClientHomeAddress,
  MeetingDetails,
  SchedulerAvailabilitySource,
  SchedulerOwningEntityType,
  SchedulerParticipant,
  SchedulerSelection,
} from '@/components/integration/nylas/scheduler/types'
import { Card, CardContent } from '@/components/ui/card'
import { MEETING_FORMAT } from '@/lib/constants'
import { cn } from '@/lib/utils'
import { PublicMeetingTypeItem } from '@/types/responses/meeting-types'

interface Props {
  primaryParticipant: SchedulerParticipant
  owningEntityType: SchedulerOwningEntityType
  owningEntityId: number | string
  onSchedulerSubmit: (meetingDetails: MeetingDetails) => Promise<void>
  hideChrome?: boolean
  /**
   * Whether the person using the scheduler is booking for themselves (affects wording). When false, the wording refers to "your client" instead.
   */
  isBookingAsClient?: boolean
  /**
   * Overrides where availability is loaded from, e.g. the reschedule
   * availability endpoint. Should be referentially stable.
   */
  availabilitySource?: SchedulerAvailabilitySource
  /**
   * Moving an existing meeting: the participants are fixed, so they're hidden
   * and can't be changed. The submitted participants should be ignored.
   */
  isRescheduling?: boolean
  /**
   * The formats and offices on offer, when the host already has them.
   * Otherwise they're loaded for the owning entity.
   */
  meetingType?: PublicMeetingTypeItem
  /**
   * The meeting being rescheduled is at the client's home, but the address is
   * hidden from this user. While it stays there, the address on file is kept
   * and isn't asked for or submitted.
   */
  hasHiddenClientHomeAddress?: boolean
  /**
   * The town or city of the hidden address, when this user may see that much
   * (e.g. guests), so they can tell which address is on file.
   */
  hiddenClientHomeTownOrCity?: string | null
  /**
   * The format and place answers to start from, as last reported through
   * `onSelectionChange`, so they survive leaving and coming back to the step.
   */
  initialSelection?: SchedulerSelection
  onSelectionChange?: (selection: SchedulerSelection) => void
  /**
   * An address the client gave earlier, offered as a one-click fill for the
   * home address fields.
   */
  suggestedClientAddress?: ClientHomeAddress | null
}

const Scheduler: React.FC<Props> = ({
  primaryParticipant,
  owningEntityType,
  owningEntityId,
  onSchedulerSubmit,
  hideChrome = false,
  isBookingAsClient = true,
  availabilitySource,
  isRescheduling = false,
  initialSelection,
  onSelectionChange,
  suggestedClientAddress = null,
  meetingType,
  hasHiddenClientHomeAddress = false,
  hiddenClientHomeTownOrCity = null,
}) => {
  const {
    configValue,
    selectionValue,
    duration,
    isLoading,
    isFetching,
    isAvailabilityFetchError,
    retry,
  } = useSchedulerState({
    primaryParticipant,
    owningEntityType,
    owningEntityId,
    availabilitySource,
    isRescheduling,
    initialSelection,
    onSelectionChange,
    suggestedClientAddress,
    meetingType,
    hasHiddenClientHomeAddress,
    hiddenClientHomeTownOrCity,
  })

  const {
    selectedDate,
    selectedDateTime,
    selectedFormat,
    selectedLocationId,
    clientHomeAddress,
  } = selectionValue
  const { timezone, setTimezone } = configValue

  // An in-person office meeting's times depend on the office, so wait for one.
  const isWaitingForOffice =
    selectedFormat === MEETING_FORMAT.in_person_office &&
    selectedLocationId === null

  const isAtClientHome = selectedFormat === MEETING_FORMAT.in_person_client_home
  const shouldSendClientHomeAddress =
    isAtClientHome && !hasHiddenClientHomeAddress
  const isMissingClientHomeAddress =
    shouldSendClientHomeAddress &&
    !isClientHomeAddressComplete(clientHomeAddress)

  function handleSchedulerSubmit() {
    const canSubmit =
      !!selectedDateTime &&
      !!selectedFormat &&
      isBookableMeetingFormat(selectedFormat) &&
      !isWaitingForOffice &&
      !isMissingClientHomeAddress

    if (!canSubmit) {
      return
    }

    onSchedulerSubmit({
      start: selectedDateTime,
      end: new Date(selectedDateTime.getTime() + (duration ?? 0) * 60000),
      participants: [selectionValue.finalPrimaryParticipant],
      timezone: configValue.timezone,
      format: selectedFormat,
      // Only an in-person meeting has an office: the one it's at, or the one
      // handling the visit to the client's home
      locationId: isInPersonMeetingFormat(selectedFormat)
        ? (selectedLocationId ?? undefined)
        : undefined,
      ...(shouldSendClientHomeAddress && {
        clientAddressLine1: clientHomeAddress.addressLine1.trim(),
        clientTownOrCity: clientHomeAddress.townOrCity.trim(),
        clientPostcode: clientHomeAddress.postcode.trim(),
      }),
    })
  }

  return (
    <SchedulerConfigContext.Provider value={configValue}>
      <SchedulerSelectionContext.Provider value={selectionValue}>
        <div className="w-200 max-w-full">
          <Card
            className={cn(
              'p-0',
              hideChrome && 'border-transparent shadow-none',
            )}
          >
            <CardContent
              className={cn('@container py-6', hideChrome ? 'px-0' : 'px-6')}
            >
              <MeetingFormatPicker isBookingAsClient={isBookingAsClient} />
              <InPersonPlacePicker isBookingAsClient={isBookingAsClient} />
              <InPersonLocationPicker />
              <ClientHomeAddressFields isBookingAsClient={isBookingAsClient} />

              {selectedFormat && !isWaitingForOffice && (
                <MeetingDatePicker
                  isLoadingAvailability={isLoading}
                  isFetchingAvailability={isFetching}
                  isAvailabilityFetchError={isAvailabilityFetchError}
                  onRetryAvailabilityFetch={retry}
                />
              )}

              {selectedDate && <TimeslotPicker duration={duration} />}

              {selectedDate && selectedDateTime && (
                <MeetingSummary onSubmit={handleSchedulerSubmit} />
              )}
            </CardContent>
          </Card>

          <div className="flex items-center justify-end mt-3">
            <TimezonePicker timezone={timezone} onChange={setTimezone} />
          </div>
        </div>
      </SchedulerSelectionContext.Provider>
    </SchedulerConfigContext.Provider>
  )
}

export default Scheduler
